<template>
  <div ref="tagsContainerRef" v-on-click-outside="closePanel" :class="$style.tagsContainer">
    <div :class="$style.inputWrapper" @click="showTagsPanel">
      <span v-if="!internalSelectedTags.length" :class="$style.addTagMessage">Add a tag</span>
      <Chip
        v-for="tag in internalSelectedTags"
        :key="tag.name"
        :label="tag.name"
        removable
        :class="$style.tagChip"
        @remove="() => removeTag(tag)"
      />
    </div>
    <VTMultiSelect
      v-if="showPanel"
      :selected-items="internalSelectedTags"
      :items="combinedTags"
      :class="$style.tagsSelector"
      @add-item="handleAddTag"
      @delete-item="handleDelete"
      @toggle-selection="handleToggleTagSelection"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Chip from "primevue/chip";
import VTMultiSelect from "@/components/VTMultiSelect";
import useTags from "@/modules/Items/hooks/useTags";
import { vOnClickOutside } from "@vueuse/components";
import { useConfirm } from "primevue/useconfirm";
import { useItems } from "@/modules/Items/hooks/useItems";

const props = defineProps({
  modelValue: Array,
  gameId: String,
  versionId: String,
});

const emit = defineEmits(["update:modelValue"]);
const showPanel = ref(false);

const { tags: gameTags, deleteTag: deleteTagMutation } = useTags(props.gameId);
const combinedTags = ref([...gameTags.value]);

const confirm = useConfirm();
const { refetchItems } = useItems(props.gameId, props.versionId);

const internalSelectedTags = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

function removeTag(tagToRemove) {
  internalSelectedTags.value = [...internalSelectedTags.value.filter((tag) => tag.name !== tagToRemove.name)];
}

function showTagsPanel() {
  showPanel.value = true;
}

function closePanel() {
  showPanel.value = false;
}

function handleDelete(tag) {
  confirm.require({
    message: `Do you want to delete the tag ${tag.name}?`,
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    acceptIcon: "",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: () => {
      deleteTag(tag);
    },
  });
}

async function deleteTag(tagToDelete) {
  try {
    await deleteTagMutation(tagToDelete.id);
    internalSelectedTags.value = internalSelectedTags.value.filter((tag) => tag.id !== tagToDelete.id);
    await refetchItems();
  } catch (error) {
    console.error(`Error deleting tag with ID '${tagToDelete.id}':`, error);
  }
}

watch(gameTags, (newTags) => {
  combinedTags.value = [...new Set([...newTags, ...internalSelectedTags.value])];
});

function handleAddTag(newTagName) {
  const trimmedTagName = newTagName.trim();
  if (!trimmedTagName || internalSelectedTags.value.some((tag) => tag.name === trimmedTagName)) {
    return;
  }

  const newTag = { name: trimmedTagName };
  internalSelectedTags.value = [...internalSelectedTags.value, newTag];
  combinedTags.value = [...new Set([...combinedTags.value, newTag])];
}

function handleToggleTagSelection(tag) {
  const index = internalSelectedTags.value.findIndex((t) => t.name === tag.name);
  if (index > -1) {
    internalSelectedTags.value.splice(index, 1);
  } else {
    internalSelectedTags.value.push(tag);
  }
  internalSelectedTags.value = [...internalSelectedTags.value];
}
</script>

<style module>
.tagsContainer {
  display: flex;
  flex-direction: column;
  position: relative;
  font-weight: 400;
}

.inputWrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border: 1px solid #ccc;
  padding: 5px;
  border-radius: 5px;
  cursor: pointer;
  min-height: 40px;
}

.tagChip {
  margin-right: 5px;
}

.tagLabel {
  margin-left: 5px;
}

.tagItemSelected {
  background-color: #e0e0e0;
}

.tagsSelector {
  position: absolute;
  width: 100%;
  top: 100%;
  left: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ccc;
  border-top: none;
}
</style>
